import { useCallback } from 'react';
import { useMutation } from '@apollo/client';
import { CONVERSATION_THREAD_COMMENT_DELETE } from '../../../utils/apollo/conversations';
import {
  IConversationThreadCommentDeleteMutation,
  IConversationThreadCommentDeleteMutationVariables,
  IThreadCommentDeleteInput,
} from '../../../utils/apollo/graphqlSchema';
import printGraphqlErrors from '../../../utils/apollo/printGraphqlErrors';
import { assertNotNil } from '../../../utils/types';
import toastMessage from '../../../utils/toastMessage';
import { useAppDispatch } from '../../../redux/hooks';
import { updateThread } from '../../../redux/slices/conversationThreads';

const useDeleteThreadComment = () => {
  const dispatch = useAppDispatch();

  const handleMutationComplete = ({
    conversationThreadCommentDelete,
  }: IConversationThreadCommentDeleteMutation) => {
    if (printGraphqlErrors(conversationThreadCommentDelete.errors)) {
      return;
    }
    const thread = conversationThreadCommentDelete.thread;
    assertNotNil(thread);
    dispatch(updateThread(thread));

    toastMessage({
      message: 'Comment has been removed.',
      type: 'info',
    });
  };

  const [performMutation, { loading }] = useMutation<
    IConversationThreadCommentDeleteMutation,
    IConversationThreadCommentDeleteMutationVariables
  >(CONVERSATION_THREAD_COMMENT_DELETE, {
    onCompleted: handleMutationComplete,
  });

  const deleteThreadComment = useCallback(
    (input: IThreadCommentDeleteInput) => {
      return performMutation({
        variables: {
          input,
        },
      });
    },
    [performMutation],
  );

  return {
    loading,
    deleteThreadComment,
  };
};

export default useDeleteThreadComment;
